import { App } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '../../components/Core/Icon';
import FilePublicLinksForm from '../../components/Forms/FilePublicLinksForm';
import FileSessionsForm from '../../components/Forms/FileSessionsForm';
import PasskeysForm from '../../components/Forms/PasskeysForm';
import UserSessionsForm from '../../components/Forms/UserSessionsForm';
import { QuickShareStatusEnum } from '../../enums/QuickShareStatusEnum';
import { UserProfileStatusEnum } from '../../enums/UserProfileStatusEnum';
import { WebSocketEventTypeEnum } from '../../enums/WebSocketEventTypeEnum';
import type File from '../../interfaces/File';
import { useAppStore } from '../../store/store';
import { useAuth } from '../useAuth';
import { useRequestLeave } from '../useRequestLeave';
import useBatches from '../useBatches';
import { useBranding } from '../useBranding';
import { useFileUpload } from '../useFileUpload';
import useFormatTimestamp from '../useFormatTimestamp';
import useNotifications from '../useNotifications';
import { useOnBehalfOf } from '../useOnBehalfOf';
import useOpenFile from '../useOpenFile';
import usePendingFileShareRequests from '../usePendingFileShareRequests';
import useQuickShare from '../useQuickShare';
import { InterfaceLimitationEnum } from '../../enums/InterfaceLimitationEnum';
import { AuthenticatedUserMenuActionEnum } from '../../enums/AuthenticatedUserMenuActionEnum';
import {
  filteredAuthenticatedUserMenuActions,
  type AuthenticatedUserMenuAction,
  type AuthenticatedUserMenuActionContext,
} from '../../lib/authenticatedUserMenuActions';
import type { MenuProps } from 'antd';
import { useKeyboardShortcutsContextOptional } from '../../contexts/KeyboardShortcutsContext';
import { openAiSummaryModal } from '../../utils/openAiSummaryModal';
import { openKeyboardShortcutsFormModal } from '../../utils/openKeyboardShortcutsFormModal';
import { openTotpFormModal } from '../../utils/openTotpFormModal';
import {
  getBrandingDataDefaultImage,
  getContrastColorForBackground,
  hasInterfaceLimitation,
  isDevEnvironment,
} from '../../utils/utils';

// Custom hook that provides all logic and data required by the header UI.
export function useHeader() {
  // Auth status and methods
  const { logout, isLoggedIn, isReader, isExternal, isAdmin, isSuperAdmin, getUserInfo } =
    useAuth();
  // File opening utility functions
  const { openFileSafely, redirectFileToLocation } = useOpenFile();
  // Authenticated user info from state (profile may be missing in unavailable mode)
  const { authenticatedUser, userProfileStatus } = useAppStore();
  // Impersonation state and exit function
  const { isOnBehalfOfUser, exitOnBehalfOfUser } = useOnBehalfOf();

  // Routing utilities
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Branding data and loading state
  const { brandingData, isLoadingBrandingData } = useBranding();
  // i18n translation function
  const { t } = useTranslation();
  // Ant Design's Modal instance
  const { modal } = App.useApp();
  const requestLeave = useRequestLeave();

  // File upload - quick share hooks
  const {
    uploadQuickShareGroupedFilesQueueWithoutRootFolder,
    isUploadingQuickShareFiles,
    uploadedQuickShareGroupedFilesAmount,
    totalQuickShareGroupedFilesAmount,
    isQuickShareUploadFilesCompletedWithoutErrors,
    quickShareGlobalProgressByBytes,
  } = useFileUpload();

  // Quick share status
  const { currentUploadStatus } = useQuickShare();
  // Pending file share requests
  const { pendingGrants } = usePendingFileShareRequests();
  // Notifications state and actions
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll } = useNotifications();
  // Active batches and refresh function
  const { activeBatches, activeCount, refreshBatches } = useBatches();
  // Timestamp formatting utility
  const formatTimestamp = useFormatTimestamp();
  // State for controlling OnBehalfOf drawer UI
  const [isOnBehalfOfDrawerOpen, setIsOnBehalfOfDrawerOpen] = useState(false);
  // State for App Debug drawer (dev-only)
  const [isAppDebugDrawerOpen, setIsAppDebugDrawerOpen] = useState(false);

  /**
   * Opens the user sessions management modal
   */
  const handleOpenSessionsModal = useCallback(() => {
    const modalInstance = modal.confirm({
      title: t('modal.userSessionsForm.activeSessions'),
      content: <UserSessionsForm onClose={() => modalInstance.destroy()} onLogout={logout} />,
      footer: null,
      width: 600,
    });
  }, [modal, t, logout]);

  /**
   * Opens the Two-Factor Authentication (TOTP) management modal
   */
  const handleOpenTotpModal = useCallback(() => {
    openTotpFormModal({ modal, t, getUserInfo });
  }, [modal, t, getUserInfo]);

  const handleOpenKeyboardShortcutsModal = useCallback(() => {
    openKeyboardShortcutsFormModal({ modal, t });
  }, [modal, t]);

  const keyboardShortcutsContext = useKeyboardShortcutsContextOptional();

  useEffect(() => {
    keyboardShortcutsContext?.registerOpenShortcutsModal(handleOpenKeyboardShortcutsModal);
    return () => keyboardShortcutsContext?.registerOpenShortcutsModal(null);
  }, [handleOpenKeyboardShortcutsModal, keyboardShortcutsContext]);

  /**
   * Opens the AI summary modal for a file (used by "summary" notifications)
   */
  const openAiSummary = useCallback(
    (file: File) => {
      openAiSummaryModal({ modal, t, file });
    },
    [modal, t],
  );

  /** Opens passkey list/register modal and refreshes profile on success. */
  const handleOpenPasskeysModal = useCallback(() => {
    const modalInstance = modal.confirm({
      content: (
        <PasskeysForm
          onClose={() => modalInstance.destroy()}
          onSuccess={() => {
            modalInstance.destroy();
            void getUserInfo?.();
          }}
        />
      ),
      footer: null,
      title: t('modal.passkeysForm.title'),
      width: 640,
    });
  }, [getUserInfo, modal, t]);

  /**
   * Opens the folder (password) unlock sessions modal
   * Shows all folder password sessions
   */
  const handleOpenFileSessionsModal = useCallback(() => {
    const modalInstance = modal.confirm({
      title: t('common.folderSessions'),
      content: <FileSessionsForm onClose={() => modalInstance.destroy()} />,
      footer: null,
      width: 600,
    });
  }, [modal, t]);

  /**
   * Opens the public links list modal (manage/revoke public share links).
   */
  const handleOpenPublicLinksModal = useCallback(() => {
    const modalInstance = modal.confirm({
      title: t('common.publicLinks'),
      content: <FilePublicLinksForm onClose={() => modalInstance.destroy()} />,
      footer: null,
      width: 600,
    });
  }, [modal, t]);

  /**
   * Dispatches header profile menu actions by key (used with authenticatedUserMenuActions).
   */
  const runHeaderActionIfCanLeave = useCallback(
    async (action: () => void | Promise<void>) => {
      const canLeave = await requestLeave();
      if (!canLeave) {
        return;
      }

      await action();
    },
    [requestLeave],
  );

  const handleHeaderMenuAction = useCallback(
    (key: AuthenticatedUserMenuActionEnum) => {
      switch (key) {
        case AuthenticatedUserMenuActionEnum.Account:
          void runHeaderActionIfCanLeave(() => navigate('/account/profile'));
          break;
        case AuthenticatedUserMenuActionEnum.Totp:
          handleOpenTotpModal();
          break;
        case AuthenticatedUserMenuActionEnum.Passkeys:
          handleOpenPasskeysModal();
          break;
        case AuthenticatedUserMenuActionEnum.AppDebugDrawer:
          if (isDevEnvironment()) {
            setIsAppDebugDrawerOpen(true);
          }
          break;
        case AuthenticatedUserMenuActionEnum.Sessions:
          handleOpenSessionsModal();
          break;
        case AuthenticatedUserMenuActionEnum.FileSessions:
          handleOpenFileSessionsModal();
          break;
        case AuthenticatedUserMenuActionEnum.PublicLinks:
          handleOpenPublicLinksModal();
          break;
        case AuthenticatedUserMenuActionEnum.StopImpersonation:
          exitOnBehalfOfUser().catch((error) => {
            console.error('Failed to exit on behalf of user:', error);
          });
          break;
        case AuthenticatedUserMenuActionEnum.ImpersonateUser:
          setIsOnBehalfOfDrawerOpen(true);
          break;
        case AuthenticatedUserMenuActionEnum.SignOut:
          void runHeaderActionIfCanLeave(() => logout());
          break;
        case AuthenticatedUserMenuActionEnum.Security:
        case AuthenticatedUserMenuActionEnum.SessionsMenu:
        case AuthenticatedUserMenuActionEnum.Divider:
          break;
      }
    },
    [
      navigate,
      handleOpenTotpModal,
      handleOpenPasskeysModal,
      handleOpenSessionsModal,
      handleOpenFileSessionsModal,
      handleOpenPublicLinksModal,
      exitOnBehalfOfUser,
      setIsOnBehalfOfDrawerOpen,
      logout,
      runHeaderActionIfCanLeave,
    ],
  );

  /**
   * Determines whether the search field should be visible in the header
   */
  const isSearchFieldVisible = useMemo(() => {
    if (!isLoggedIn) return false;

    // Hide on email routes (email has its own search in EmailsTable)
    if (location.pathname.startsWith('/emails')) return false;

    // Show on all other authenticated routes
    return true;
  }, [location.pathname, isLoggedIn]);

  // Title showing status of quick share job
  const quickShareTitle: string = useMemo(() => {
    if (currentUploadStatus === QuickShareStatusEnum.ShareError) {
      return t('errors.filesNotShared');
    }

    // No files left to upload or not uploading
    if (!isUploadingQuickShareFiles) {
      return t('common.uploadedFilesCounter', {
        amount: uploadedQuickShareGroupedFilesAmount,
        total: totalQuickShareGroupedFilesAmount,
      });
    } else {
      return t('common.uploadingFilesCounter', {
        amount: uploadedQuickShareGroupedFilesAmount,
        total: totalQuickShareGroupedFilesAmount,
      });
    }
  }, [
    currentUploadStatus,
    isUploadingQuickShareFiles,
    uploadedQuickShareGroupedFilesAmount,
    totalQuickShareGroupedFilesAmount,
    t,
  ]);

  // True if on the /quick-share route
  const isInQuickShareRoute = useMemo(() => {
    return location.pathname === '/quick-share';
  }, [location.pathname]);

  // True if on the /login route
  const isInLoginRoute = useMemo(() => {
    return location.pathname === '/login';
  }, [location.pathname]);

  // True if on a public link route (/files/public/:token or /files/public/:token/folder/:folderId)
  const isInPublicLinkRoute = useMemo(() => {
    return location.pathname.startsWith('/files/public');
  }, [location.pathname]);

  // True if on viewer route with publicFileToken in query string (public file link view)
  const isInViewerWithPublicToken = useMemo(() => {
    const match = /^\/viewer\/(\d+)$/.exec(location.pathname);
    return !!match && searchParams.get('publicFileToken') !== null;
  }, [location.pathname, searchParams]);

  // Cache-bust only when branding (or logo URL) changes. Using Date.now() here would run on every
  // render and cause an infinite refetch loop because Header re-renders very often.
  const logoSrc = useMemo(() => {
    const baseUrl = brandingData?.topBarLogo
      ? brandingData.topBarLogo
      : getBrandingDataDefaultImage('top-bar');
    if (!baseUrl) return '';
    const separator = baseUrl.includes('?') ? '&' : '?';
    const version = brandingData?.hash ?? '';
    return version ? `${baseUrl}${separator}v=${version}` : baseUrl;
  }, [brandingData?.topBarLogo, brandingData?.hash]);

  // True if user has only external or reader permissions
  const isExternalOrReader = useMemo(() => {
    return isExternal || isReader;
  }, [isExternal, isReader]);

  // Context for header profile menu actions (visibility and labels)
  const headerMenuContext: AuthenticatedUserMenuActionContext = useMemo(
    () => ({
      pathname: location.pathname,
      isExternalOrReader,
      isOnBehalfOfUser,
      canShowImpersonate:
        !hasInterfaceLimitation(
          authenticatedUser?.settings || [],
          InterfaceLimitationEnum.DenyShowOnBehalfOf,
        ) && !isOnBehalfOfUser,
    }),
    [location.pathname, isExternalOrReader, authenticatedUser?.settings, isOnBehalfOfUser],
  );

  // Creates the dropdown menu items for the user's header profile menu
  const menuItems = useMemo((): MenuProps['items'] => {
    const mapActionToMenuItem = (
      action: AuthenticatedUserMenuAction,
    ): NonNullable<MenuProps['items']>[number] => {
      if (action.type === 'divider') {
        return { type: 'divider' };
      }
      const label = action.getLabel ? (
        action.getLabel(headerMenuContext, t)
      ) : action.icon && action.labelKey ? (
        <div className="flex items-center gap-2">
          {action.icon}
          <span>{t(action.labelKey)}</span>
        </div>
      ) : action.labelKey ? (
        t(action.labelKey)
      ) : undefined;
      return {
        key: action.key,
        label,
        danger: action.danger,
        onClick: action.children ? undefined : () => handleHeaderMenuAction(action.key),
        children: action.children?.map((child) => {
          const childLabelContent =
            child.labelKey && child.icon ? (
              <div className="flex items-center gap-2">
                {child.icon}
                <span>{t(child.labelKey)}</span>
              </div>
            ) : child.labelKey ? (
              t(child.labelKey)
            ) : (
              child.label
            );
          const isChildActive = child.isActive?.(headerMenuContext) ?? false;

          return {
            key: child.key,
            label: isChildActive ? (
              <div className="flex w-full items-center justify-between gap-2">
                {childLabelContent}
                <Icon name="CircleCheckIcon" className="text-azure h-4 w-4 shrink-0" />
              </div>
            ) : (
              childLabelContent
            ),
            onClick: () => handleHeaderMenuAction(child.key as AuthenticatedUserMenuActionEnum),
          };
        }),
      };
    };

    const actions = filteredAuthenticatedUserMenuActions(headerMenuContext);
    const actionItems = actions.map(mapActionToMenuItem);
    // Align with Header avatar fallback when profile/role is missing
    const profileRoleLabel =
      authenticatedUser?.role != null ? t(`roles.${authenticatedUser.role}`) : 'N/D';
    const canRetryProfile =
      userProfileStatus === UserProfileStatusEnum.Stale ||
      userProfileStatus === UserProfileStatusEnum.Unavailable;

    return [
      {
        disabled: true,
        key: 'profile',
        label: profileRoleLabel,
      },
      ...(canRetryProfile
        ? [
            {
              key: 'retry-profile',
              label: t('errors.userProfileRetry'),
              onClick: () => {
                void getUserInfo();
              },
            },
          ]
        : []),
      ...actionItems,
    ];
  }, [
    authenticatedUser?.role,
    getUserInfo,
    handleHeaderMenuAction,
    headerMenuContext,
    t,
    userProfileStatus,
  ]);

  // Maps quick share upload status to icon name string
  const quickShareStatusIcon = useMemo((): string => {
    switch (currentUploadStatus) {
      case QuickShareStatusEnum.ConnectionError:
        return 'UnplugIcon';
      case QuickShareStatusEnum.Error:
        return 'ServerCrashIcon';
      case QuickShareStatusEnum.SomeErrors:
      case QuickShareStatusEnum.ShareError:
        return 'TriangleAlertIcon';
      case QuickShareStatusEnum.Completed:
        return 'CircleCheckIcon';
      default:
        return '';
    }
  }, [currentUploadStatus]);

  // Returns proper icon name for bell based on notification event type
  const getNotificationIcon = useCallback(
    (eventType: string): 'TagIcon' | 'FilesIcon' | 'AiFileIcon' | 'ShareIcon' | 'BellIcon' => {
      switch (eventType) {
        case WebSocketEventTypeEnum.Tags:
          return 'TagIcon';
        case WebSocketEventTypeEnum.Summary:
          return 'FilesIcon';
        case WebSocketEventTypeEnum.DocumentClassification:
          return 'AiFileIcon';
        case WebSocketEventTypeEnum.SharingOperation:
          return 'ShareIcon';
        default:
          return 'BellIcon';
      }
    },
    [],
  );

  // Listen for custom event and call batch refresh when triggered
  useEffect(() => {
    // Handler for the refresh event: triggers batch data refresh
    const handleRefreshBatches = () => {
      refreshBatches();
    };

    // Subscribe to the event on global appStore
    const unsubscribeRefreshBatches = useAppStore
      .getState()
      .events.on.refreshBatches(handleRefreshBatches);

    // Cleanup on unmount
    return () => {
      unsubscribeRefreshBatches();
    };
  }, [refreshBatches]);

  const headerBackgroundHex = brandingData?.topBarColorBackground ?? '#193057';
  const isDevMode = isDevEnvironment();
  // Use user-configured foreground (icons, borders, text) or default white, same as SettingCompanyBranding preview
  //const headerIconColor = brandingData?.topBarColorForeground ?? '#ffffff';
  const headerIconColor = getContrastColorForBackground(
    brandingData?.topBarColorBackground ?? '#193057',
  );

  // Expose all necessary data/functions to the header component/UI
  return {
    // Auth
    logout,
    isLoggedIn,
    isReader,
    isExternal,
    isAdmin,
    isSuperAdmin,
    // User data
    authenticatedUser,
    // On behalf of
    isOnBehalfOfUser,
    exitOnBehalfOfUser,
    isOnBehalfOfDrawerOpen,
    setIsOnBehalfOfDrawerOpen,
    isAppDebugDrawerOpen,
    setIsAppDebugDrawerOpen,
    // Location
    location,
    navigate,
    // Branding
    brandingData,
    isLoadingBrandingData,
    // File upload
    uploadQuickShareGroupedFilesQueueWithoutRootFolder,
    isUploadingQuickShareFiles,
    uploadedQuickShareGroupedFilesAmount,
    totalQuickShareGroupedFilesAmount,
    isQuickShareUploadFilesCompletedWithoutErrors,
    quickShareGlobalProgressByBytes,
    // Quick share
    currentUploadStatus,
    // Pending grants
    pendingGrants,
    // Notifications
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAll,
    // Batches
    activeBatches,
    activeCount,
    refreshBatches,
    // Format
    formatTimestamp,
    // File operations
    openFileSafely,
    redirectFileToLocation,
    openAiSummary,
    // Computed values
    isSearchFieldVisible,
    quickShareTitle,
    isInQuickShareRoute,
    isInLoginRoute,
    isInPublicLinkRoute,
    isInViewerWithPublicToken,
    logoSrc,
    isExternalOrReader,
    menuItems,
    quickShareStatusIcon,
    getNotificationIcon,
    // Header styling
    headerBackgroundHex,
    headerIconColor,
    isDevEnvironment: isDevMode,
  };
}
